import { useEffect, useRef, useState, useCallback } from 'react'
import { useFeedStore } from '../stores/feedStore'
import { useAccountStore } from '../stores/accountStore'
import { useAlertStore } from '../stores/alertStore'
import { useSSE } from '../hooks/useSSE'
import TweetCard from './TweetCard'
import { cn } from '../lib/utils'

function FeedList() {
  const {
    tweets,
    isLoading,
    error,
    hasMore,
    filters,
    autoScroll,
    fetchTweets,
    setFilters,
    clearFilters,
    setAutoScroll,
    addTweet,
  } = useFeedStore()

  const { accounts } = useAccountStore()
  const { keywords } = useAlertStore()
  const [searchQuery, setSearchQuery] = useState('')
  const feedRef = useRef<HTMLDivElement>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Connect to SSE for real-time updates
  const { isConnected } = useSSE({
    onTweet: (tweet) => {
      addTweet(tweet)
      if (autoScroll && feedRef.current) {
        feedRef.current.scrollTop = 0
      }
    },
  })

  // Initial fetch
  useEffect(() => {
    fetchTweets(true)
  }, [fetchTweets, filters])

  // Infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          fetchTweets()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [hasMore, isLoading, fetchTweets])

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      setFilters({ search: searchQuery || undefined })
    },
    [searchQuery, setFilters]
  )

  const handleAccountFilter = useCallback(
    (accountId: number) => {
      const current = filters.accounts || []
      const updated = current.includes(accountId)
        ? current.filter((id) => id !== accountId)
        : [...current, accountId]
      setFilters({ accounts: updated.length > 0 ? updated : undefined })
    },
    [filters.accounts, setFilters]
  )

  const handleKeywordFilter = useCallback(
    (keywordId: number) => {
      const current = filters.keywords || []
      const updated = current.includes(keywordId)
        ? current.filter((id) => id !== keywordId)
        : [...current, keywordId]
      setFilters({ keywords: updated.length > 0 ? updated : undefined })
    },
    [filters.keywords, setFilters]
  )

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Feed
          </h2>
          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <div className="flex items-center gap-2 text-sm">
              <span
                className={cn(
                  'w-2 h-2 rounded-full',
                  isConnected ? 'bg-green-500' : 'bg-red-500'
                )}
              />
              <span className="text-gray-500 dark:text-gray-400">
                {isConnected ? 'Live' : 'Disconnected'}
              </span>
            </div>

            {/* Auto-scroll toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Auto-scroll
              </span>
            </label>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tweets..."
            className="input flex-1"
          />
          <button type="submit" className="btn-primary">
            Search
          </button>
          {(filters.search || filters.accounts || filters.keywords || filters.only_alerts) && (
            <button
              type="button"
              onClick={clearFilters}
              className="btn-secondary"
            >
              Clear
            </button>
          )}
        </form>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          {/* Account filters */}
          {accounts.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                Accounts:
              </span>
              {accounts.map((account) => (
                <button
                  key={account.id}
                  onClick={() => handleAccountFilter(account.id)}
                  className={cn(
                    'px-2 py-0.5 text-xs rounded-full border transition-colors',
                    filters.accounts?.includes(account.id)
                      ? 'bg-primary-100 border-primary-500 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-primary-500'
                  )}
                >
                  @{account.username}
                </button>
              ))}
            </div>
          )}

          {/* Keyword filters */}
          {keywords.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                Keywords:
              </span>
              {keywords.map((kw) => (
                <button
                  key={kw.id}
                  onClick={() => handleKeywordFilter(kw.id)}
                  className={cn(
                    'px-2 py-0.5 text-xs rounded-full border transition-colors',
                    filters.keywords?.includes(kw.id)
                      ? 'border-transparent'
                      : 'border-gray-300 dark:border-gray-600'
                  )}
                  style={{
                    backgroundColor: filters.keywords?.includes(kw.id)
                      ? kw.color
                      : undefined,
                  }}
                >
                  {kw.keyword}
                </button>
              ))}
            </div>
          )}

          {/* Alerts only toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.only_alerts || false}
              onChange={(e) =>
                setFilters({ only_alerts: e.target.checked || undefined })
              }
              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Alerts only
            </span>
          </label>
        </div>
      </header>

      {/* Feed */}
      <div ref={feedRef} className="flex-1 overflow-y-auto p-4">
        {error && (
          <div className="p-4 mb-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}

        {tweets.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">
              No tweets yet. Add some accounts to monitor!
            </p>
          </div>
        )}

        <div className="space-y-4">
          {tweets.map((tweet) => (
            <TweetCard key={tweet.tweet_id} tweet={tweet} />
          ))}
        </div>

        {/* Load more trigger */}
        <div ref={loadMoreRef} className="py-4 text-center">
          {isLoading && (
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <svg
                className="w-5 h-5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Loading...
            </div>
          )}
          {!hasMore && tweets.length > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No more tweets
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default FeedList
